import { useRef, type PointerEvent as RPointerEvent } from 'react';
import { cropClip, imageBoxStyle } from '../components/note/NoteCard';
import { useAssetUrl } from '../components/note/useAssetUrl';
import type { NoteImage } from '../storage/schemas';

interface Props {
  images: NoteImage[];
  above: boolean;
  aspect: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Live update while dragging (no undo step per frame). */
  onChange: (id: string, patch: Partial<NoteImage>, commit: boolean) => void;
  cardRef: React.RefObject<HTMLDivElement | null>;
  interactive: boolean;
}

type Gesture =
  | { kind: 'move'; id: string; startX: number; startY: number; x0: number; y0: number }
  | { kind: 'scale'; id: string; cx: number; cy: number; d0: number; w0: number }
  | { kind: 'rotate'; id: string; cx: number; cy: number; a0: number; r0: number }
  | { kind: 'pinch'; id: string; d0: number; a0: number; w0: number; r0: number };

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

function EditableImage({ img, aspect, selected, onPointerDown, onHandleDown, interactive }: {
  img: NoteImage;
  aspect: number;
  selected: boolean;
  onPointerDown: (e: RPointerEvent, img: NoteImage) => void;
  onHandleDown: (e: RPointerEvent, img: NoteImage, kind: 'scale' | 'rotate') => void;
  interactive: boolean;
}) {
  const url = useAssetUrl(img.assetId);
  return (
    <div
      className={`nc-image is-editable ${selected ? 'is-selected' : ''} ${img.locked ? 'is-locked' : ''}`}
      style={{ ...imageBoxStyle(img, aspect), pointerEvents: interactive ? 'auto' : 'none' }}
      onPointerDown={(e) => onPointerDown(e, img)}
      role="button"
      tabIndex={interactive ? 0 : -1}
      aria-label={`Image${img.locked ? ' (locked)' : ''}${selected ? ', selected' : ''}`}
      aria-pressed={selected}
    >
      {url ? <img src={url} alt="" draggable={false} style={{ clipPath: cropClip(img) }} /> : <div className="nc-image-loading" />}
      {selected && !img.locked && (
        <>
          <span className="img-handle is-rotate" onPointerDown={(e) => onHandleDown(e, img, 'rotate')} aria-hidden="true" />
          <span className="img-handle is-scale" onPointerDown={(e) => onHandleDown(e, img, 'scale')} aria-hidden="true" />
        </>
      )}
    </div>
  );
}

export function EditableImages({ images, above, aspect, selectedId, onSelect, onChange, cardRef, interactive }: Props) {
  const gesture = useRef<Gesture | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const list = images.filter((i) => i.aboveText === above).sort((a, b) => a.z - b.z);
  if (!list.length) return null;

  const cardRect = () => cardRef.current?.getBoundingClientRect() ?? new DOMRect(0, 0, 1, 1);
  const centerOf = (img: NoteImage) => {
    const r = cardRect();
    return { cx: r.left + img.x * r.width, cy: r.top + (img.y * r.width) / aspect };
  };

  const track = (img: NoteImage, target: Element, pointerId: number) => {
    (target as HTMLElement).setPointerCapture?.(pointerId);
    const move = (ev: PointerEvent) => {
      pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      const g = gesture.current;
      if (!g) return;
      const r = cardRect();
      if (g.kind === 'move') {
        const dx = (ev.clientX - g.startX) / r.width;
        const dy = (ev.clientY - g.startY) / (r.width / aspect);
        onChange(g.id, { x: clamp(g.x0 + dx, -0.2, 1.2), y: clamp(g.y0 + dy, -0.3, 1.3) }, false);
      } else if (g.kind === 'scale') {
        const d = Math.hypot(ev.clientX - g.cx, ev.clientY - g.cy);
        onChange(g.id, { w: clamp((g.w0 * d) / Math.max(g.d0, 1), 0.05, 1.6) }, false);
      } else if (g.kind === 'rotate') {
        const a = Math.atan2(ev.clientY - g.cy, ev.clientX - g.cx);
        let deg = g.r0 + ((a - g.a0) * 180) / Math.PI;
        // Snap near right angles.
        for (const s of [-180, -90, 0, 90, 180]) if (Math.abs(deg - s) < 4) deg = s;
        onChange(g.id, { rotation: Math.round(deg * 10) / 10 }, false);
      } else if (g.kind === 'pinch' && pointers.current.size >= 2) {
        const [p1, p2] = [...pointers.current.values()];
        const d = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const a = Math.atan2(p2.y - p1.y, p2.x - p1.x);
        onChange(g.id, { w: clamp((g.w0 * d) / Math.max(g.d0, 1), 0.05, 1.6), rotation: Math.round((g.r0 + ((a - g.a0) * 180) / Math.PI) * 10) / 10 }, false);
      }
    };
    const up = (ev: PointerEvent) => {
      pointers.current.delete(ev.pointerId);
      if (pointers.current.size === 0) {
        if (gesture.current) onChange(gesture.current.id, {}, true);
        gesture.current = null;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    void img;
  };

  const onPointerDown = (e: RPointerEvent, img: NoteImage) => {
    if (!interactive) return;
    e.stopPropagation();
    if (img.locked) {
      onSelect(img.id);
      return;
    }
    e.preventDefault();
    onSelect(img.id);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && gesture.current) {
      const [p1, p2] = [...pointers.current.values()];
      gesture.current = { kind: 'pinch', id: img.id, d0: Math.hypot(p2.x - p1.x, p2.y - p1.y), a0: Math.atan2(p2.y - p1.y, p2.x - p1.x), w0: img.w, r0: img.rotation };
      return;
    }
    gesture.current = { kind: 'move', id: img.id, startX: e.clientX, startY: e.clientY, x0: img.x, y0: img.y };
    track(img, e.currentTarget, e.pointerId);
  };

  const onHandleDown = (e: RPointerEvent, img: NoteImage, kind: 'scale' | 'rotate') => {
    e.stopPropagation();
    e.preventDefault();
    const { cx, cy } = centerOf(img);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current =
      kind === 'scale'
        ? { kind: 'scale', id: img.id, cx, cy, d0: Math.hypot(e.clientX - cx, e.clientY - cy), w0: img.w }
        : { kind: 'rotate', id: img.id, cx, cy, a0: Math.atan2(e.clientY - cy, e.clientX - cx), r0: img.rotation };
    track(img, e.currentTarget, e.pointerId);
  };

  return (
    <div className={`nc-images ${above ? 'is-above' : 'is-below'} ${interactive ? 'is-interactive' : ''}`}>
      {list.map((img) => (
        <EditableImage key={img.id} img={img} aspect={aspect} selected={img.id === selectedId} onPointerDown={onPointerDown} onHandleDown={onHandleDown} interactive={interactive} />
      ))}
    </div>
  );
}
